const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
const CALENDAR_DATE_PATTERN = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/

/**
 * @param {number} value
 * @returns {string}
 */
function padTwoDigits(value) {
    const text = String(value)
    return text.length < 2 ? '0' + text : text
}

/**
 * Parsea YYYY-MM-DD como fecha local, evitando el desfase UTC de `new Date(iso)`.
 *
 * @param {string} value
 * @returns {Date|null}
 */
export function parseIsoDate(value) {
    if (!value || typeof value !== 'string') {
        return null
    }

    const match = value.match(DATE_PATTERN)
    if (!match) {
        return null
    }

    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    const parsed = new Date(year, month, day)

    if (parsed.getFullYear() !== year || parsed.getMonth() !== month || parsed.getDate() !== day) {
        return null
    }

    return parsed
}

/**
 * @param {string} value
 * @returns {{ date: Date|null, time: string }}
 */
export function parseNativeDateValue(value) {
    if (!value || typeof value !== 'string') {
        return { date: null, time: '' }
    }

    const datetimeMatch = value.match(DATETIME_PATTERN)
    if (datetimeMatch) {
        return {
            date: parseIsoDate(datetimeMatch[1] + '-' + datetimeMatch[2] + '-' + datetimeMatch[3]),
            time: datetimeMatch[4] + ':' + datetimeMatch[5]
        }
    }

    return {
        date: parseIsoDate(value.slice(0, 10)),
        time: ''
    }
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatIsoDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return ''
    }

    return date.getFullYear() + '-' + padTwoDigits(date.getMonth() + 1) + '-' + padTwoDigits(date.getDate())
}

/**
 * Serializa al contrato nativo: `YYYY-MM-DD` o `YYYY-MM-DDTHH:mm`.
 *
 * @param {Date|null} date
 * @param {string} time
 * @param {'date'|'datetime'} mode
 * @returns {string}
 */
export function serializeNativeDateValue(date, time, mode) {
    const isoDate = formatIsoDate(date)
    if (!isoDate) {
        return ''
    }

    if (mode !== 'datetime') {
        return isoDate
    }

    const safeTime = time && /^\d{2}:\d{2}$/.test(time) ? time : '00:00'
    return isoDate + 'T' + safeTime
}

/**
 * vue-functional-calendar emite fechas sin ceros a la izquierda (`2026-5-12`).
 *
 * @param {string} value
 * @returns {Date|null}
 */
export function parseCalendarDate(value) {
    if (!value || typeof value !== 'string') {
        return null
    }

    const padded = parseIsoDate(value)
    if (padded) {
        return padded
    }

    const match = value.match(CALENDAR_DATE_PATTERN)
    if (!match) {
        return null
    }

    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    const parsed = new Date(year, month, day)

    if (parsed.getFullYear() !== year || parsed.getMonth() !== month || parsed.getDate() !== day) {
        return null
    }

    return parsed
}

/**
 * Evento `change` compatible con `<input type="date">`.
 *
 * @param {string} nextValue
 * @param {Function} [onRestore]
 * @returns {{ target: { value: string } }}
 */
export function createNativeLikeChangeEvent(nextValue, onRestore) {
    let currentValue = nextValue

    return {
        target: {
            get value() {
                return currentValue
            },
            set value(restored) {
                currentValue = restored
                if (typeof onRestore === 'function') {
                    onRestore(restored)
                }
            }
        }
    }
}
